// ---------------------------------------------------------------------------
// OverviewTab — main orchestrator for the Client Overview page
// ---------------------------------------------------------------------------

import React, { useCallback, useEffect, useMemo } from "react";
import { useHistory } from "react-router-dom";
import {
  Box,
  Button,
  Card,
  Chip,
  Divider,
  IconButton,
  List,
  ListItem,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Stack,
  Typography,
} from "@mui/material";

import AddIcon from "@mui/icons-material/Add";

import { DealGraph, InfoCard } from "components/reports";
import type { DealGraph as DealGraphType } from "store/types";

import {
  useEntityAllocations,
  useLinkedDeals,
  usePortfolioKpis,
  useTasksSummary,
  useNavTimeSeries,
} from "hooks/useClientOverview";

import { useActionHook } from "hooks";
import { useReportsQueriesSelector, useSnapshotCubicConfigSelector, useReportsActions } from "store";
import type { ICubic } from "store/types";

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

export interface OverviewTabProps {
  clientId: string;
  isTeamViewer?: boolean;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export const OverviewTab: React.FC<OverviewTabProps> = ({
  clientId,
  isTeamViewer: _isTeamViewer = false,
}) => {
  const history = useHistory();

  const numericClientId = Number(clientId);
  const isInvalidClientId = !clientId || isNaN(numericClientId);

  const { data: kpis, isLoading: kpisLoading, isError: kpisError } = usePortfolioKpis(numericClientId);
  const { data: entities, isLoading: entitiesLoading, isError: entitiesError } = useEntityAllocations(numericClientId);
  const { data: deals, isLoading: dealsLoading, isError: dealsError } = useLinkedDeals(numericClientId);
  const { data: tasks, isLoading: tasksLoading, isError: tasksError } = useTasksSummary(numericClientId);
  const { data: navTimeSeriesData, isLoading: navLoading, isError: navError } = useNavTimeSeries(numericClientId);

  // ---- Safe Data Unwrapping ----
  // This ensures the UI doesn't break whether TanStack Query returns {data: ...} or just the array/object
  const entitiesList = entities?.data || entities || [];
  const entitiesCount = entities?.total_count || entitiesList.length || 0;

  const dealsList = deals?.data || deals || [];
  const dealsCount = deals?.total_count || dealsList.length || 0;

  const tasksData = tasks?.data || tasks || {};
  const recentTasks = tasksData.recent_tasks || [];
  const openTasksCount = tasksData.open_count || 0;

  // ---- Cubic Snapshot (configurable KPI cards) ----
  const cubicSnapshot = useSnapshotCubicConfigSelector();
  const { setQueriesAction, getCubicAction, getMetaAction } = useReportsActions();
  const { data: cubicData, action: getCubic, loading: cubicLoading } = useActionHook(getCubicAction);
  const { filters, exchangeMethod, currency, dateRange, use_accounting_date } = useReportsQueriesSelector();

  const DEFAULT_CUBIC_SNAPSHOT: ICubic = useMemo(() => ({
    kpi_commitment: { title: "Total Commitment", filter: "capital_invested" },
    kpi_nav:        { title: "Total NAV",         filter: "nav" },
    kpi_moic:       { title: "MOIC",              filter: "weighted_return" },
    kpi_irr:        { title: "IRR",               filter: "irr" },
  }), []);

  // Initialize cubicSnapshot defaults on mount
  useEffect(() => {
    if (Object.keys(cubicSnapshot).length === 0) {
      setQueriesAction({ cubicSnapshot: DEFAULT_CUBIC_SNAPSHOT });
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Fetch metadata for KPIFields on mount
  useEffect(() => {
    getMetaAction();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Re-fetch cubic data whenever config or global query params change
  useEffect(() => {
    if (Object.keys(cubicSnapshot).length > 0) {
      getCubic({
        cubics: cubicSnapshot,
        pageType: "cubicSnapshot",
        filters: { ...(filters ?? {}), client_id: [numericClientId] },
        exchangeMethod,
        currency,
        dateRange,
        use_accounting_date,
      });
    }
  }, [cubicSnapshot, dateRange, exchangeMethod, currency, filters, use_accounting_date, numericClientId]);

  const isLoading = kpisLoading || entitiesLoading || dealsLoading || tasksLoading || navLoading;
  const isError = kpisError || entitiesError || dealsError || tasksError || navError;
  const isEmpty = entitiesList.length === 0;

  // ---- NAV Graph Data (fetched from backend) ----
  const navGraphData: DealGraphType = useMemo(() => {
    const navTimeSeries = navTimeSeriesData?.nav_over_time || [];
    
    if (navTimeSeries.length === 0) {
      return {
        title: "NAV over time",
        xAxis: [{ categories: [] }],
        yAxis: [{ labels: { format: "{value}" }, title: { text: "NAV (USD)" } }],
        series: [{
          type: "spline",
          name: "Portfolio NAV",
          data: [],
          yAxis: 0,
          tooltip: { valueSuffix: "" }
        }]
      };
    }

    const dates = navTimeSeries.map((point) => point.date);
    const values = navTimeSeries.map((point) => point.value);

    return {
      title: "NAV over time",
      xAxis: [{ categories: dates }],
      yAxis: [{ labels: { format: "{value}" }, title: { text: "NAV (USD)" } }],
      series: [{
        type: "spline",
        name: "Portfolio NAV",
        data: values,
        yAxis: 0,
        tooltip: { valueSuffix: "" }
      }]
    };
  }, [navTimeSeriesData]);

  const handleDealRowClick = useCallback((dealId: number) => {
    history.push(`/main/deal/${dealId}`);
  }, [history]);

  // 1. Invalid State
  if (isInvalidClientId) {
    return (
      <Box sx={{ p: 4, textAlign: "center", minHeight: "200px" }}>
        <Typography variant="h6">Invalid client ID</Typography>
      </Box>
    );
  }

  // 2. Error State
  if (isError) {
    return (
      <Box sx={{ p: 4, textAlign: "center", minHeight: "200px" }}>
        <Typography variant="h6" sx={{ mb: 2 }}>Couldn't load client data. Please try again.</Typography>
        <Button variant="contained" onClick={() => window.location.reload()}>Retry</Button>
      </Box>
    );
  }

  // 3. Loading State
  if (isLoading) {
    return (
      <Box>
        <Stack spacing={3}>
          <Stack direction="row" spacing={2}>
            {[1, 2, 3, 4].map((i) => <Skeleton key={i} variant="rounded" width="100%" height={80} sx={{ flex: "1 0 0" }} />)}
          </Stack>
          <Skeleton variant="rounded" width="100%" height={240} />
        </Stack>
      </Box>
    );
  }

  // 4. Populated State
  return (
    <Box
      data-testid="overview-tab"
      sx={{
        minHeight: "100%",
        display: "flex",
        flexDirection: "column",
        gap: 3,
      }}
    >
      {/* Widget 1: KPI Strip */}
      <Stack
        direction="row"
        gap={3}
        sx={{
          "& .info-card": {
            boxShadow: "0px 2px 8px rgba(18, 18, 23, 0.10)",
          },
        }}
      >
        {Object.entries(cubicData ?? DEFAULT_CUBIC_SNAPSHOT).map(([name, data]) => (
          <Box key={name} sx={{ flex: 1 }}>
            <InfoCard
              pageType="cubicSnapshot"
              name={name}
              isLoading={cubicLoading}
              {...(cubicData
                ? (data as any)
                : { title: (data as ICubic[string]).title, filter: (data as ICubic[string]).filter, value: "\u2014", apply_number_format: false }
              )}
              configurable={true}
            />
          </Box>
        ))}
      </Stack>

      {/* Widget 2.5: NAV Graph + Tasks (split row) */}
      <Stack direction="row" gap={3} sx={{ width: "100%" }}>
        {/* Left: NAV Graph */}
        <Card
          sx={{
            flex: 2,
            bgcolor: "background.paper",
            boxShadow: "sm",
            borderRadius: 16,
            p: 3,
          }}
        >
          <Box
            sx={{
              height: 400,
              "& .line-graph": {
                height: "100%",
                display: "flex",
                flexDirection: "column",
              },
              "& .measure-graph__chart-block": {
                minHeight: "unset",
                height: "100%",
                flex: 1,
              },
            }}
          >
            <DealGraph loading={navLoading} graphData={navGraphData} />
          </Box>
        </Card>

        {/* Right: Tasks */}
        <Card
          sx={{
            flex: 1,
            bgcolor: "background.paper",
            boxShadow: "sm",
            borderRadius: 16,
            p: 3,
          }}
        >
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            sx={{ mb: 2 }}
          >
            <Typography variant="body1" sx={{ fontWeight: 500 }}>
              Tasks ({openTasksCount} open)
            </Typography>
            <Stack direction="row" alignItems="center" gap={1}>
              <IconButton size="small" color="primary" aria-label="Add task">
                <AddIcon fontSize="small" />
              </IconButton>
              <Button
                variant="text"
                size="small"
                color="secondary"
                onClick={() => history.push("/main/task_management")}
              >
                View all &rarr;
              </Button>
            </Stack>
          </Stack>
          {recentTasks.length > 0 ? (
            <List disablePadding>
              {recentTasks.map((task: any, index: number) => (
                <React.Fragment key={task.task_id}>
                  <ListItem
                    disableGutters
                    button
                    onClick={() => history.push(`/main/task_management?task=${task.task_id}`)}
                    sx={{
                      px: 2, 
                      py: 3, 
                      alignItems: "flex-start", 
                      flexDirection: "column", 
                      borderBottom: "1px solid rgba(0, 0, 0, 0.1)", 
                      width: "100%",
                      boxSizing: "border-box", // Ensures padding doesn't break the 100% width
                      cursor: "pointer",
                      "&:hover": { bgcolor: "action.hover" },
                    }}
                  >
                    {/* Top row: Title, Status (centered if date exists), and Date */}
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        width: "100%",
                        gap: 1,
                      }}
                    >
                      {/* Left: Task Title */}
                      <Typography
                        variant="body2"
                        sx={{
                          color: "text.primary",
                          fontWeight: 500,
                          flex: task.due_date ? 1 : "initial",
                        }}
                      >
                        {task.title}
                      </Typography>

                      {/* Middle (centered if date exists) or Right (if no date): Status Chip */}
                      <Chip
                        label={task.status || "Open"}
                        size="small"
                        sx={{
                          bgcolor: "rgba(7, 162, 125, 0.15)",
                          color: "success.dark",
                          fontWeight: 500,
                          fontSize: "11px",
                          height: 20,
                          textTransform: "capitalize",
                          ...(task.due_date
                            ? { ml: "auto", mr: "auto" }
                            : { ml: "auto" }),
                        }}
                      />

                      {/* Right: Due Date (only shown when date exists) */}
                      {task.due_date && (
                        <Typography
                          variant="body4"
                          sx={{ color: "text.secondary", whiteSpace: "nowrap", textAlign: "right" }}
                        >
                          {task.due_date}
                        </Typography>
                      )}
                    </Box>
                    
                    {/* Bottom row: Assignee name */}
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        width: "100%",
                        mt: 1,
                      }}
                    >
                      {task.assignee_name && (
                        <Typography variant="body4" sx={{ color: "text.secondary" }}>
                          {task.assignee_name}
                        </Typography>
                      )}
                    </Box>
                  </ListItem>
                  {index < recentTasks.length - 1 && (
                    <Divider sx={{ borderColor: "#DBE8F5" }} />
                  )}
                </React.Fragment>
              ))}
            </List>
          ) : (
            <Typography variant="body2" sx={{ color: "text.secondary" }}>
              No tasks yet.
            </Typography>
          )}
        </Card>
      </Stack>

      {/* Widget 2: Entities */}
      <Card
        sx={{
          bgcolor: "background.paper",
          boxShadow: "sm",
          borderRadius: 16,
          p: 3,
        }}
      >
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          sx={{ mb: 2 }}
        >
          <Typography variant="body1" sx={{ fontWeight: 500 }}>
            Entities ({entitiesCount})
          </Typography>
          <Button variant="text" size="small" onClick={() => {}}>
            Manage &rarr;
          </Button>
        </Stack>
        {isEmpty ? (
          <Typography variant="body2" sx={{ color: "text.secondary" }}>
            No entities linked yet.
          </Typography>
        ) : (
          <List disablePadding>
            {entitiesList.slice(0, 4).map((entity: any, index: number) => (
              <React.Fragment key={entity.investor_id}>
                <ListItem
                  disableGutters
                  button
                  onClick={() => history.push(`/main/portfolio-construction/investor/${entity.investor_id}`)}
                  sx={{
                    px: 0,
                    py: 1.5,
                    flexDirection: "column",
                    alignItems: "stretch",
                    cursor: "pointer",
                    "&:hover": { bgcolor: "action.hover" },
                  }}
                >
                  <Box display="flex" justifyContent="space-between" alignItems="center" width="100%">
                    <Stack direction="column" gap={0.5}>
                      <Typography variant="body2" color="text.primary">
                        {entity.investor_name}
                      </Typography>
                      {entity.entity_type && entity.entity_type.toLowerCase() !== "unknown" && (
                        <Typography variant="body4" color="text.secondary">
                          {entity.entity_type}
                        </Typography>
                      )}
                    </Stack>
                    <Chip
                      size="small"
                      label={`${entity.allocation_percentage}%`}
                      sx={{
                        bgcolor: (theme: any) => theme.palette.blue[25],
                        color: "text.secondary",
                        fontWeight: 500,
                      }}
                    />
                  </Box>
                </ListItem>
                {index < entitiesList.slice(0, 4).length - 1 && (
                  <Divider sx={{ borderColor: "divider" }} />
                )}
              </React.Fragment>
            ))}
          </List>
        )}
      </Card>

      {/* Widget 3: Linked Deals */}
      <Card
        sx={{
          bgcolor: "background.paper",
          boxShadow: "sm",
          borderRadius: 16,
          pt: 3,
          px: 0,
          pb: 0,
        }}
      >
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          sx={{ mb: 2, px: 3 }}
        >
          <Typography variant="body1" sx={{ fontWeight: 500 }}>
            Linked deals ({dealsCount})
          </Typography>
          <Button
            variant="text"
            size="small"
            onClick={() => history.push("/main/my-deals")}
          >
            View all &rarr;
          </Button>
        </Stack>
        {dealsList.length > 0 ? (
          <TableContainer sx={{ boxShadow: "none", border: "none" }}>
            <Table size="small" sx={{ width: "100%", tableLayout: "fixed" }}>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ width: "30%", borderColor: "divider", pb: 1 }}>
                    <Typography variant="body4" sx={{ color: "text.secondary", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.5px" }}>
                      DEAL
                    </Typography>
                  </TableCell>
                  <TableCell sx={{ width: "20%", borderColor: "divider", pb: 1 }}>
                    <Typography variant="body4" sx={{ color: "text.secondary", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.5px" }}>
                      ASSET CLASS
                    </Typography>
                  </TableCell>
                  {["COMMITMENT", "NAV", "MOIC", "IRR"].map((header) => (
                    <TableCell key={header} align="right" sx={{ borderColor: "divider", pb: 1 }}>
                      <Typography variant="body4" sx={{ color: "text.secondary", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.5px" }}>
                        {header}
                      </Typography>
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {dealsList.slice(0, 5).map((deal: any) => (
                  <TableRow
                    key={deal.deal_id}
                    hover
                    onClick={() => handleDealRowClick(deal.deal_id)}
                    sx={{ cursor: "pointer", "& td": { borderColor: "divider" } }}
                  >
                    <TableCell>
                      <Typography variant="body2" color="text.primary">{deal.deal_name}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">{deal.status || "\u2014"}</Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2" color="text.primary">
                        {deal.invested_capital != null ? `$${Number(deal.invested_capital).toLocaleString()}` : "\u2014"}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2" color="text.primary">
                        {deal.nav != null ? `$${Number(deal.nav).toLocaleString()}` : "\u2014"}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2" color="text.primary">
                        {deal.moic != null ? `${deal.moic}x` : "\u2014"}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2" color="text.secondary">{"\u2014"}</Typography>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        ) : (
          <Typography variant="body2" sx={{ color: "text.secondary" }}>
            No linked deals yet.
          </Typography>
        )}
      </Card>

    </Box>
  );
};

export default OverviewTab;
